import { Component, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { DropdownMultiselectItem } from '@bayclubs/drop-down';
import { camps } from '../../models/models.generated';
import { FilterApiService } from '../../services/api/filter-api.service';
import { FilterDropDownsService } from '../../services/filter-dropdowns.service';
import { CampOptionsSelectorComponent } from './camp-options-selector/camp-options-selector.component';
import { CampCategoryService } from './services/camp-category.service';

@Component({
    selector: 'app-manage-images',
    host: { 'data-testid': 'manage-images-page' },
    templateUrl: './manage-images.component.html',
    styleUrl: './manage-images.component.scss',
    standalone: false,
})
export class ManageImagesComponent implements OnInit, OnDestroy {
    private filtersService = inject(FilterDropDownsService);
    private filtersApi = inject(FilterApiService);
    private categoryService = inject(CampCategoryService);
    private optionsSelector = viewChild(CampOptionsSelectorComponent);

    isBusy = signal<boolean>(false);

    selectedCampTypes = signal<DropdownMultiselectItem[]>([]);
    selectedParentCategories = signal<DropdownMultiselectItem[]>([]);

    parentCategoryDropdownItems = signal<DropdownMultiselectItem[]>([]);
    campTypeDropdownItems = signal<DropdownMultiselectItem[]>([]);

    ngOnInit(): void {
        this.isBusy.set(true);
        this.loadFilters();
    }

    ngOnDestroy(): void {
        this.categoryService.cleanupService();
    }

    onCampTypesChanged(newTypes: DropdownMultiselectItem[]): void {
        this.adjustCategoryDropdownItems(newTypes);
        this.selectedCampTypes.set(newTypes);
    }

    onParentCategoriesChanged(newCategories: DropdownMultiselectItem[]): void {
        this.selectedParentCategories.set(newCategories);
        this.adjustCampTypeDropdownItems();
    }

    private loadFilters(): void {
        this.filtersApi.get(false).subscribe((data) => {
            if (data.isSuccess) {
                data.value.categories.sort((a, b) => a.name.localeCompare(b.name));
                this.categoryService.initializeService(data.value.categories);
                const parentCategoriesOnly = this.categoryService.getParentCategories();
                const categoryDropdownItems = this.filtersService.createCategoriesDropdownItems(parentCategoriesOnly);

                this.parentCategoryDropdownItems.set(categoryDropdownItems);

                const campTypeDropdownItems = this.filtersService.createCampTypeDropDownItems([]);
                campTypeDropdownItems.sort((a, b) => a.displayName.localeCompare(b.displayName));
                this.campTypeDropdownItems.set(campTypeDropdownItems);
            }

            this.isBusy.set(false);
            this.selectedCampTypes.set(this.campTypeDropdownItems());
            this.selectedParentCategories.set(this.parentCategoryDropdownItems());
        });
    }

    private adjustCategoryDropdownItems(newCampTypes: DropdownMultiselectItem[]): void {
        const allTypes: DropdownMultiselectItem[] = this.campTypeDropdownItems();
        const parentCategoryDropdowns: DropdownMultiselectItem[] = this.parentCategoryDropdownItems();
        if (allTypes.length === newCampTypes.length) {
            parentCategoryDropdowns.forEach((cat) => (cat.selected = true));
            this.selectedParentCategories.set(parentCategoryDropdowns);
            this.optionsSelector().adjustAllSelectedForCategories();

            return;
        }

        const oldCampTypes: DropdownMultiselectItem[] = this.selectedCampTypes();
        const parentCategories: camps.CategoryModel[] = this.categoryService.getParentCategories();
        const categoriesDropdownItems: DropdownMultiselectItem[] = this.parentCategoryDropdownItems();
        if (oldCampTypes.length > newCampTypes.length) {
            const removedTypes: DropdownMultiselectItem[] = oldCampTypes.filter((oldType) => !newCampTypes.some((newType) => newType.id === oldType.id));
            const categoriesForRemovedTypes: camps.CategoryModel[] = parentCategories.filter((x) => removedTypes.some((t) => t.id === x.campType));
            categoriesDropdownItems.forEach((x) => {
                if (categoriesForRemovedTypes.some((c) => c.id === x.id)) {
                    x.selected = false;
                }
            });

            this.optionsSelector().adjustAllSelectedForCategories();

            return;
        }

        if (oldCampTypes.length < newCampTypes.length) {
            const currentParentCategories = this.selectedParentCategories();
            const addedTypes: DropdownMultiselectItem[] = newCampTypes.filter((newType) => !oldCampTypes.some((oldType) => oldType.id === newType.id));
            const categoriesForAddedTypes: camps.CategoryModel[] = parentCategories.filter((x) => addedTypes.some((t) => t.id === x.campType));
            const categoryDropdownItemsForAddedTypes: DropdownMultiselectItem[] = categoriesDropdownItems.filter((x) => {
                const isAddedCategory = categoriesForAddedTypes.some((c) => c.id === x.id);
                if (isAddedCategory) {
                    x.selected = true;
                    return true;
                }
            });

            const newSelectedCategories = [...currentParentCategories, ...categoryDropdownItemsForAddedTypes];
            this.selectedParentCategories.set(newSelectedCategories);
            this.optionsSelector().adjustAllSelectedForCategories();

            return;
        }
    }

    private adjustCampTypeDropdownItems(): void {
        const selectedCategories: DropdownMultiselectItem[] = this.selectedParentCategories();
        const parentCategories: camps.CategoryModel[] = this.categoryService.getParentCategories();
        const allTypes: DropdownMultiselectItem[] = this.campTypeDropdownItems();

        const selectedParentCategories: camps.CategoryModel[] = parentCategories.filter((x) => selectedCategories.some((sc) => sc.id === x.id));
        const filteredTypes: DropdownMultiselectItem[] = allTypes.filter((type) => {
            // every type must be selected
            const allCategoriesSelected: boolean = parentCategories.length === selectedParentCategories.length;
            if (allCategoriesSelected) {
                type.selected = true;
                return true;
            }

            // no type must be selected
            const nothingSelected: boolean = selectedParentCategories.length === 0;
            if (nothingSelected) {
                type.selected = false;
                return false;
            }

            // type remains selected when it has no categories, so category selection does not affect its state
            const typeHasAnyCategory: boolean = parentCategories.some((cat) => cat.campType === type.id);
            if (!typeHasAnyCategory) {
                if (type.selected) {
                    return true;
                } else {
                    return false;
                }
            }

            // type selected when any category is selected for it
            const anyCategorySelected: boolean = selectedParentCategories.some((cat) => cat.campType === type.id);
            if (!anyCategorySelected) {
                type.selected = false;
                return false;
            }

            type.selected = true;
            return true;
        });

        this.selectedCampTypes.set(filteredTypes);
        this.optionsSelector().adjustAllSelectedForCampTypes();
    }
}
